/**
 * Presentation Layer - Auth Guard Provider
 * Validates session integrity on mount before rendering protected content.
 *
 * This is the primary defense against "stale session" scenarios:
 * when a user closes the browser and returns later with an expired JWT,
 * the guard detects it and redirects to login before any Dashboard
 * rendering or API calls happen.
 */

"use client";

import { useEffect, useState, type PropsWithChildren } from "react";
import { usePathname } from "next/navigation";
import { storageClient } from "@/shared/core/clients";
import { isTokenExpired } from "@/shared/core/utils";
import { COOKIE_NAMES, ROUTES } from "@/constants";
import { clearSelectedOrganization } from "@/shared/core/utils";
import { routeUtils } from "@/shared/core/routes";

/**
 * Clear all auth-related data from cookies and localStorage.
 */
const performFullSessionCleanup = (): void => {
  storageClient.deleteCookie(COOKIE_NAMES.AUTH_TOKEN);
  storageClient.deleteCookie(COOKIE_NAMES.REFRESH_TOKEN);
  storageClient.deleteCookie(COOKIE_NAMES.USER_DATA);
  clearSelectedOrganization();
};

/**
 * AuthGuard
 *
 * On mount, validates the stored auth token:
 * 1. If no token exists → skip (middleware handles unauthenticated access)
 * 2. If token exists but is expired or malformed → clear session, redirect to login
 * 3. If token is valid → render children normally
 *
 * Skips validation entirely on public/auth routes (login, signup, etc.)
 * so those pages always render without interference.
 */
export function AuthGuard({ children }: PropsWithChildren) {
  const pathname = usePathname();
  const [isValidated, setIsValidated] = useState(false);

  useEffect(() => {
    // Skip validation on public/auth routes — they don't need a session
    if (routeUtils.isAuthPath(pathname) || routeUtils.isPublicPath(pathname)) {
      setIsValidated(true);
      return;
    }

    const token = storageClient.getCookie(COOKIE_NAMES.AUTH_TOKEN);

    // No token at all — let middleware handle the redirect
    if (!token) {
      setIsValidated(true);
      return;
    }

    // Token exists — check if it's expired
    if (isTokenExpired(token)) {
      performFullSessionCleanup();
      // Hard redirect (not router.push) to bypass any cached React state
      window.location.href = ROUTES.LOGIN;
      return; // Don't set isValidated — component will unmount on redirect
    }

    // Token is valid
    setIsValidated(true);
  }, [pathname]);

  // While validating, render nothing to prevent any flash of protected content
  if (!isValidated) {
    return null;
  }

  return <>{children}</>;
}
